import { useState } from 'react';
import type { ReactElement } from 'react';

import { get, post } from 'utils/network';
import { ApiRoute, ErrorMessage } from 'config';

import { useUpcomingMenu } from 'contexts/UpcomingMenu';
import type { IUpcomingMenu } from 'models/UpcomingMenu';
import type IApiResponse from 'models/IApiResponse';
import UpcomingMenu from 'models/UpcomingMenu';

import ErrorModal from 'components/shared/modals/ErrorModal';
import useCurrentUser from 'hooks/useCurrentUser';

import SkipLabel from './SkipLabel';
import AutoSkipModal from '../modals/AutoSkipModal';
import SkipSurveyModal from '../modals/SkipModal';
import AutoSkipTooltip from './AutoSkipTooltip';

type Props = {
  item: UpcomingMenu;
  labelClassName?: string;
  showAutoSkipTooltip: boolean;
};

function SkipWeekButton({
  item,
  labelClassName,
  showAutoSkipTooltip,
}: Props): ReactElement {
  const { dispatch } = useUpcomingMenu();
  const [showSkipSurveyModal, setShowSkipSurveyModal] = useState(false);
  const [showAutoSkipModal, setShowAutoSkipModal] = useState(false);
  const [showSoldOutError, setShowSoldOutError] = useState(false);
  const [showTooltip, setShowTooltip] = useState(showAutoSkipTooltip);

  const { currentUser } = useCurrentUser();

  const handleAutoSkipWeekButton = () => {
    setShowTooltip(false);
    setShowAutoSkipModal(true);
  };

  const onChange = async () => {
    if (currentUser?.subscription.delivery_frequency === 'one_off' && !item.isEnabled) {
      handleAutoSkipWeekButton();
      return;
    }

    const action = item.isEnabled ? 'skip' : 'unskip';
    const path = `${ApiRoute.CARTS}/${item.cart_id}/${action}`;
    const {
      ok,
      status,
      parsedBody,
    } = await post<IApiResponse>(path);

    if (ok) {
      dispatch({
        type: 'toggleEnabled',
        isEnabled: !item.isEnabled,
      });
      setShowSkipSurveyModal(action === 'skip');
      dispatch({
        type: 'reload',
        // @ts-expect-error TODO: fix null safety
        data: (await get<IUpcomingMenu>(
          `${ApiRoute.UPCOMING_MENUS}/${item.menu_id}`,
        )).parsedBody,
      });
    } else if (status === 422 && parsedBody?.message === 'Menu sold out') {
      setShowSoldOutError(true);
    }
  };
  const linkText = item.isEnabled ? 'Skip' : 'Unskip';
  return (
    <div className="c-order__skip">
      <SkipLabel item={item} className={labelClassName} />

      {item.isModifiable && (
        <>
          <AutoSkipTooltip show={showTooltip} onClose={() => setShowTooltip(false)}>
            <div>
              <button
                type="button"
                onClick={onChange}
                className="c-button"
              >
                {linkText}
              </button>
            </div>
          </AutoSkipTooltip>

          <SkipSurveyModal
            show={showSkipSurveyModal}
            onClose={() => setShowSkipSurveyModal(false)}
          />

          <AutoSkipModal show={showAutoSkipModal} onClose={() => setShowAutoSkipModal(false)} />
        </>
      )}

      {showSoldOutError && (
        <ErrorModal message={ErrorMessage.SOLD_OUT_ON_UNSKIP} />
      )}
    </div>
  );
}

export default SkipWeekButton;
